import { useState, useEffect, Fragment } from 'react';

/**
 * Security Monitor (Domain C) live panel — points at this app's security-monitor
 * worker (per-app model). Worker URL is set in the UI (localStorage). Renders the
 * SOC view: KPIs, threat distribution, live feed with per-threat action buttons,
 * top offender IPs, watch-an-IP. Falls back to demo data when no worker is set.
 */

type Threat = { ip: string; country?: string; level: 'critical' | 'high' | 'suspicious' | 'low';
  score: number; reasons: string[]; suggested: string; auto?: boolean; ts?: number };
type Summary = { total: number; counts: Record<string, number>; events: Threat[]; top_ips: Threat[] };

const DEMO: Summary = {
  total: 11, counts: { critical: 2, high: 2, suspicious: 3, low: 4 },
  top_ips: [
    { ip: '185.220.101.5', country: 'RU', level: 'critical', score: 125, reasons: ['SQL-injection tool (sqlmap)', 'SQL injection payload', 'geo RU not in allow-list'], suggested: 'block+kill_sessions', auto: true },
    { ip: '45.137.21.9', country: 'NL', level: 'critical', score: 100, reasons: ['dangerous upload: shell.php'], suggested: 'block+kill_sessions', auto: true },
    { ip: '103.207.36.2', country: 'IN', level: 'high', score: 80, reasons: ['25 failed logins', 'against admin path'], suggested: 'block' },
    { ip: '20.171.207.4', country: 'US', level: 'high', score: 70, reasons: ['XSS / cookie-theft payload'], suggested: 'block' },
    { ip: '66.249.66.1', country: 'US', level: 'suspicious', score: 45, reasons: ['AI crawler (GPTBot)'], suggested: 'challenge' },
    { ip: '34.102.5.7', country: 'US', level: 'suspicious', score: 40, reasons: ['low bot score 12', 'rate 410/min'], suggested: 'challenge' },
  ],
  events: [],
};
DEMO.events = [...DEMO.top_ips,
  { ip: '8.8.8.8', country: 'US', level: 'low', score: 0, reasons: ['normal traffic'], suggested: 'allow' },
  { ip: '24.5.10.2', country: 'CA', level: 'low', score: 0, reasons: ['normal traffic'], suggested: 'allow' }];

const BADGE: Record<string, string> = {
  critical: 'text-rose-600 bg-rose-50', high: 'text-amber-600 bg-amber-50',
  suspicious: 'text-yellow-700 bg-yellow-50', low: 'text-emerald-600 bg-emerald-50',
};
const Pill = ({ l }: { l: string }) => <span className={`px-2 rounded-full text-xs font-bold ${BADGE[l] || ''}`}>{l.toUpperCase()}</span>;

// ---- Domain B: Cloudflare edge posture view ----
type Posture = { configured: boolean; note?: string; zone?: string; score?: number; gaps?: number;
  targets?: Record<string, string>; checks?: { setting: string; now: string; want: string; ok: boolean }[] };
function PostureView({ base }: { base: string }) {
  const [p, setP] = useState<Posture | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const load = async () => {
    const b = base.replace(/\/+$/, '');
    if (!b) { setP(null); setErr('Set the Monitor worker URL above to read live Cloudflare posture.'); return; }
    try { const r = await fetch(b + '/posture'); setP(await r.json()); setErr(null); }
    catch { setErr('worker unreachable'); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [base]);
  if (err) return <div className="card p-3 text-xs text-amber-700">{err}</div>;
  if (!p) return <div className="card p-3 text-xs text-gray-500">Loading posture…</div>;
  if (!p.configured) return <div className="card p-3 text-xs text-gray-600">Cloudflare not configured on this monitor (no zone token). Target hardening: {Object.entries(p.targets || {}).map(([k, v]) => `${k}=${v}`).join(' · ')}.</div>;
  return (
    <div className="space-y-3">
      <div className="grid grid-cols-3 gap-2">
        <div className="card p-3"><div className={`text-2xl font-extrabold ${(p.score || 0) >= 90 ? 'text-emerald-600' : (p.score || 0) >= 60 ? 'text-amber-600' : 'text-rose-600'}`}>{p.score}%</div><div className="text-[11px] uppercase tracking-wide text-gray-500">Edge hardening</div></div>
        <div className="card p-3"><div className="text-2xl font-extrabold text-rose-600">{p.gaps}</div><div className="text-[11px] uppercase tracking-wide text-gray-500">Gaps</div></div>
        <div className="card p-3"><div className="text-sm font-mono text-gray-600 truncate">{p.zone}</div><div className="text-[11px] uppercase tracking-wide text-gray-500">Zone</div></div>
      </div>
      <div className="card p-0 overflow-hidden">
        <div className="p-2 text-xs uppercase tracking-wide text-gray-500 border-b">Cloudflare edge settings vs. maximum‑security target</div>
        <table className="w-full text-xs">
          <thead><tr className="text-gray-400 text-left"><th className="px-3 py-1">Setting</th><th>Current</th><th>Target</th><th>Status</th></tr></thead>
          <tbody>
            {(p.checks || []).map((c, i) => (
              <tr key={i} className="border-t border-gray-50">
                <td className="px-3 py-1 font-mono">{c.setting}</td>
                <td className="font-mono">{String(c.now)}</td>
                <td className="font-mono text-gray-500">{String(c.want)}</td>
                <td>{c.ok ? <span className="text-emerald-600 font-semibold">✓ ok</span> : <span className="text-rose-600 font-semibold">✗ fix</span>}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="text-[11px] text-gray-500">Read‑only audit. Enforce with <span className="font-mono">security cloudflare enforce --apply --geo US_CANADA</span> (Domain B).</div>
    </div>
  );
}

// ---- Domain A: code-audit results view ----
type Finding = { id: string; title: string; severity: string; cwe?: string; owasp?: string; file?: string; line?: number };
type Audit = { posted?: boolean; note?: string; posted_at?: number; stacks?: string[]; counts?: Record<string, number>; findings?: Finding[] };
const SEVCLS: Record<string, string> = { critical: 'text-rose-600 bg-rose-50', high: 'text-amber-600 bg-amber-50', medium: 'text-yellow-700 bg-yellow-50', low: 'text-gray-500 bg-gray-50' };
function AuditView({ base }: { base: string }) {
  const [a, setA] = useState<Audit | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const load = async () => {
    const b = base.replace(/\/+$/, '');
    if (!b) { setA(null); setErr('Set the Monitor worker URL above to read the latest code‑audit results.'); return; }
    try { const r = await fetch(b + '/audit'); setA(await r.json()); setErr(null); }
    catch { setErr('worker unreachable'); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [base]);
  if (err) return <div className="card p-3 text-xs text-amber-700">{err}</div>;
  if (!a) return <div className="card p-3 text-xs text-gray-500">Loading audit…</div>;
  if (!a.posted) return (
    <div className="card p-3 text-xs text-gray-600 space-y-1">
      <div>No code‑audit posted yet. Run the Domain A scanner and push results from CI:</div>
      <pre className="bg-gray-50 p-2 rounded font-mono text-[11px] overflow-x-auto">security scan . --format json | \
  curl -X POST {base || '<monitor-url>'}/audit-ingest \
  -H "x-monitor-secret: $INTERNAL_SECRET" --data-binary @-</pre>
    </div>
  );
  const ct = a.counts || {};
  return (
    <div className="space-y-3">
      <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
        {[['Findings', (a.findings || []).length, ''], ['Critical', ct.critical || 0, 'text-rose-600'], ['High', ct.high || 0, 'text-amber-600'], ['Medium', ct.medium || 0, 'text-yellow-700'], ['Low', ct.low || 0, 'text-gray-500']].map(([l, n, cls]) => (
          <div key={l as string} className="card p-3"><div className={`text-2xl font-extrabold ${cls as string}`}>{n as number}</div><div className="text-[11px] uppercase tracking-wide text-gray-500">{l as string}</div></div>
        ))}
      </div>
      <div className="text-[11px] text-gray-500">Stacks: {(a.stacks || []).join(', ') || '—'} · last scan {a.posted_at ? new Date(a.posted_at).toLocaleString() : '—'}</div>
      <div className="card p-0 overflow-hidden">
        <div className="p-2 text-xs uppercase tracking-wide text-gray-500 border-b">Findings (highest severity first)</div>
        <table className="w-full text-xs">
          <thead><tr className="text-gray-400 text-left"><th className="px-3 py-1">Severity</th><th>Control</th><th>Where</th><th>CWE / OWASP</th></tr></thead>
          <tbody>
            {(a.findings || []).slice(0, 200).map((f, i) => (
              <tr key={i} className="border-t border-gray-50">
                <td className="px-3 py-1"><span className={`px-2 rounded-full text-[10px] font-bold ${SEVCLS[f.severity] || ''}`}>{(f.severity || '').toUpperCase()}</span></td>
                <td className="font-mono">{f.id}<div className="text-gray-400 font-sans">{f.title}</div></td>
                <td className="font-mono text-gray-500">{f.file}{f.line ? ':' + f.line : ''}</td>
                <td className="text-gray-400">{f.cwe} {f.owasp}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ---- Domain C: active application sessions (click a row → username + details) ----
type Session = { id: string; username?: string; role?: string; ip?: string; country?: string;
  device?: string; ua?: string; started?: number | string; lastSeen?: number | string;
  risk?: 'low' | 'suspicious' | 'high' | 'critical'; reasons?: string[] };
const DEMO_SESSIONS: Session[] = [
  { id: 'sess_9f3a2b71', username: 'ahmed@triple-network.com', role: 'super-admin', ip: '24.5.10.2', country: 'US', device: 'Chrome · Windows', started: Date.now() - 36e5, lastSeen: Date.now() - 12e4, risk: 'low', reasons: ['MFA verified', 'allow-listed geo'] },
  { id: 'sess_4c81de09', username: 'dispatch@longbeach.gov', role: 'agent', ip: '66.249.66.1', country: 'US', device: 'Edge · Windows', started: Date.now() - 9e6, lastSeen: Date.now() - 3e5, risk: 'low', reasons: ['normal traffic'] },
  { id: 'sess_a17b55e2', username: 'svc_report', role: 'service', ip: '34.102.5.7', country: 'US', device: 'API token', started: Date.now() - 18e5, lastSeen: Date.now() - 6e4, risk: 'suspicious', reasons: ['rate 410/min', 'low bot score 12'] },
  { id: 'sess_props88', username: 'unknown (pre-auth)', role: 'guest', ip: '185.220.101.5', country: 'RU', device: 'curl/8.4', started: Date.now() - 3e5, lastSeen: Date.now() - 2e4, risk: 'critical', reasons: ['geo RU not in allow-list', 'sqlmap UA seen on same IP'] },
];
function SessionsView({ base }: { base: string }) {
  const [list, setList] = useState<Session[]>(DEMO_SESSIONS);
  const [live, setLive] = useState(false);
  const [open, setOpen] = useState<string | null>(null);
  const [killed, setKilled] = useState<Set<string>>(new Set());
  const [note, setNote] = useState<string | null>(null);
  const load = async () => {
    const b = base.replace(/\/+$/, '');
    if (!b) { setList(DEMO_SESSIONS); setLive(false); setNote('No worker URL set — showing demo sessions. Set the Monitor worker URL (Live Monitor tab) to read real sessions.'); return; }
    try {
      const r = await fetch(b + '/sessions'); const d = await r.json();
      if (d.configured && (d.sessions || []).length) { setList(d.sessions); setLive(true); setNote(null); }
      else { setList(DEMO_SESSIONS); setLive(false); setNote(d.configured ? 'No active sessions reported by the app hook.' : 'App session hook (APP_SESSION_HOOK) not set on this monitor — showing demo sessions.'); }
    } catch { setList(DEMO_SESSIONS); setLive(false); setNote('worker unreachable — showing demo sessions'); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [base]);
  const fmt = (t?: number | string) => !t ? '—' : new Date(typeof t === 'number' ? t : Date.parse(t)).toLocaleTimeString();
  const kill = async (s: Session) => {
    const b = base.replace(/\/+$/, '');
    if (b) {
      try { const r = await fetch(b + '/action', { method: 'POST', headers: { 'content-type': 'application/json' }, body: JSON.stringify({ ip: s.ip, action: 'kill_session', sessionId: s.id }) }); const d = await r.json(); alert(`kill_session ${s.username} (${s.id}) →\n` + JSON.stringify(d.results || d, null, 1)); }
      catch (e) { alert('kill failed: ' + e); }
    } else { alert(`(demo) kill session ${s.id} — ${s.username}`); }
    const nk = new Set(killed); nk.add(s.id); setKilled(nk);
  };
  return (
    <div className="space-y-3">
      <div className="card p-3 flex items-center gap-2 text-sm">
        <span className={`px-2 py-0.5 rounded text-xs font-semibold ${live ? 'bg-emerald-50 text-emerald-600' : 'bg-gray-100 text-gray-500'}`}>{live ? 'LIVE' : 'DEMO'}</span>
        <span className="text-gray-500">{list.length} active session{list.length === 1 ? '' : 's'}</span>
        <button onClick={load} className="ml-auto border rounded px-3 py-1 text-sm">↻ Refresh</button>
      </div>
      {note && <div className="card p-2 text-xs text-amber-700">{note}</div>}
      <div className="card p-0 overflow-hidden">
        <table className="w-full text-xs">
          <thead><tr className="text-gray-400 text-left"><th className="px-3 py-1">User</th><th>Session</th><th>IP / Country</th><th>Started</th><th>Risk</th><th>Actions</th></tr></thead>
          <tbody>
            {list.map((s) => (
              <Fragment key={s.id}>
                <tr className={`border-t border-gray-50 cursor-pointer hover:bg-gray-50 ${killed.has(s.id) ? 'opacity-50 line-through' : ''}`} onClick={() => setOpen(open === s.id ? null : s.id)}>
                  <td className="px-3 py-1"><span className="text-gray-400 mr-1">{open === s.id ? '▾' : '▸'}</span><span className="font-semibold">{s.username || '—'}</span> <span className="text-gray-400">{s.role}</span></td>
                  <td className="font-mono text-gray-500">{s.id}</td>
                  <td><span className="font-mono">{s.ip}</span> <span className="text-gray-400">{s.country}</span></td>
                  <td className="text-gray-500">{fmt(s.started)}</td>
                  <td><Pill l={s.risk || 'low'} /></td>
                  <td><button onClick={(e) => { e.stopPropagation(); kill(s); }} className="text-xs px-2 py-0.5 rounded border border-rose-400 text-rose-700 font-semibold">Kill session</button></td>
                </tr>
                {open === s.id && (
                  <tr className="bg-gray-50/60"><td colSpan={6} className="px-6 py-2">
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-x-6 gap-y-1 text-[12px]">
                      <div><div className="text-gray-400 text-[10px] uppercase">Username</div><div className="font-semibold">{s.username || '—'}</div></div>
                      <div><div className="text-gray-400 text-[10px] uppercase">Role</div><div>{s.role || '—'}</div></div>
                      <div><div className="text-gray-400 text-[10px] uppercase">Session ID</div><div className="font-mono">{s.id}</div></div>
                      <div><div className="text-gray-400 text-[10px] uppercase">IP / Country</div><div className="font-mono">{s.ip} · {s.country || '—'}</div></div>
                      <div><div className="text-gray-400 text-[10px] uppercase">Device</div><div>{s.device || s.ua || '—'}</div></div>
                      <div><div className="text-gray-400 text-[10px] uppercase">Started</div><div>{fmt(s.started)}</div></div>
                      <div><div className="text-gray-400 text-[10px] uppercase">Last activity</div><div>{fmt(s.lastSeen)}</div></div>
                      <div><div className="text-gray-400 text-[10px] uppercase">Risk</div><div><Pill l={s.risk || 'low'} /></div></div>
                    </div>
                    {(s.reasons || []).length > 0 && <div className="mt-1 text-[11px] text-gray-500">Signals: {(s.reasons || []).join(' · ')}</div>}
                  </td></tr>
                )}
              </Fragment>
            ))}
          </tbody>
        </table>
      </div>
      <div className="text-[11px] text-gray-500">Click a row to reveal the session's username and details. Sessions are read live from the app via <span className="font-mono">APP_SESSION_HOOK</span>; Kill session ends that one session (the app hook revokes its token).</div>
    </div>
  );
}

export default function SecurityMonitorPanel({ area = 'monitor' }: { area?: string }) {
  const [base, setBase] = useState<string>(() => localStorage.getItem('secMonApi') || '');
  const [data, setData] = useState<Summary>(DEMO);
  const [live, setLive] = useState(false);
  const [watch, setWatch] = useState('');
  const [blocked, setBlocked] = useState<Set<string>>(new Set());
  const [err, setErr] = useState<string | null>(null);

  const load = async () => {
    const b = base.replace(/\/+$/, '');
    if (!b) { setData(DEMO); setLive(false); return; }
    try {
      const r = await fetch(b + '/summary');
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      setData(await r.json()); setLive(true); setErr(null);
    } catch (e) { setData(DEMO); setLive(false); setErr('worker unreachable — showing demo data'); }
  };
  useEffect(() => { if (area === 'monitor' || area === 'offenders') load(); /* eslint-disable-next-line */ }, []);

  const setUrl = (v: string) => { setBase(v); localStorage.setItem('secMonApi', v.replace(/\/+$/, '')); };

  const act = async (ip: string, action: string) => {
    const b = base.replace(/\/+$/, '');
    if (b) {
      try {
        const r = await fetch(b + '/action', { method: 'POST', headers: { 'content-type': 'application/json' }, body: JSON.stringify({ ip, action }) });
        const d = await r.json();
        alert(`${action} ${ip} →\n` + JSON.stringify(d.results || d, null, 1));
      } catch (e) { alert('action failed: ' + e); }
    } else { alert(`(demo) ${action} ${ip}`); }
    const nb = new Set(blocked);
    if (action === 'allow') nb.delete(ip); else nb.add(ip);
    setBlocked(nb);
  };

  const c = data.counts || {};
  const tot = Math.max(1, (c.critical || 0) + (c.high || 0) + (c.suspicious || 0) + (c.low || 0));
  const feed = (data.events || []).filter((e) => !watch || e.ip.includes(watch));

  const Acts = ({ t }: { t: Threat }) => (
    <div className="flex flex-wrap gap-1">
      {t.suggested.includes('block') && <button onClick={() => act(t.ip, 'block')} className="text-xs px-2 py-0.5 rounded border border-rose-300 text-rose-600">Block</button>}
      {t.suggested.includes('kill') && <button onClick={() => act(t.ip, 'block+kill_sessions')} className="text-xs px-2 py-0.5 rounded border border-rose-400 text-rose-700 font-semibold">Kill sessions</button>}
      {t.suggested === 'challenge' && <button onClick={() => act(t.ip, 'challenge')} className="text-xs px-2 py-0.5 rounded border border-amber-300 text-amber-600">Challenge</button>}
      <button onClick={() => act(t.ip, 'deny')} className="text-xs px-2 py-0.5 rounded border border-rose-300 text-rose-600">Deny</button>
      <button onClick={() => act(t.ip, 'allow')} className="text-xs px-2 py-0.5 rounded border border-emerald-300 text-emerald-600">Allow</button>
    </div>
  );

  return (
    <div className="space-y-3">
      <div className="card p-3 flex flex-wrap items-center gap-2 text-sm">
        <span className={`px-2 py-0.5 rounded text-xs font-semibold ${live ? 'bg-emerald-50 text-emerald-600' : 'bg-gray-100 text-gray-500'}`}>{live ? 'LIVE' : 'DEMO'}</span>
        <label className="text-gray-500">Monitor worker URL</label>
        <input className="flex-1 border rounded px-2 py-1 text-xs font-mono" placeholder="https://security-monitor-trinethub.<sub>.workers.dev" value={base} onChange={(e) => setUrl(e.target.value)} />
        <input className="border rounded px-2 py-1 text-xs" placeholder="watch IP…" value={watch} onChange={(e) => setWatch(e.target.value)} />
        <button onClick={load} className="border rounded px-3 py-1 text-sm">↻ Refresh</button>
      </div>
      {area === 'sessions' && <SessionsView base={base} />}
      {area === 'posture' && <PostureView base={base} />}
      {area === 'audit' && <AuditView base={base} />}
      {(area === 'monitor' || area === 'offenders') && (<>
      {err && <div className="card p-2 text-xs text-amber-700">{err}</div>}

      <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
        {[['Total', data.total, ''], ['Critical', c.critical || 0, 'text-rose-600'], ['High', c.high || 0, 'text-amber-600'],
          ['Suspicious', c.suspicious || 0, 'text-yellow-700'], ['Low/Allowed', c.low || 0, 'text-emerald-600']].map(([l, n, cls]) => (
          <div key={l as string} className="card p-3"><div className={`text-2xl font-extrabold ${cls as string}`}>{n as number}</div><div className="text-[11px] uppercase tracking-wide text-gray-500">{l as string}</div></div>
        ))}
      </div>

      <div className="card p-3">
        <div className="text-xs uppercase tracking-wide text-gray-500 mb-1">Threat distribution</div>
        <div className="flex h-3 rounded overflow-hidden">
          <span className="bg-rose-500" style={{ width: `${100 * (c.critical || 0) / tot}%` }} />
          <span className="bg-amber-500" style={{ width: `${100 * (c.high || 0) / tot}%` }} />
          <span className="bg-yellow-400" style={{ width: `${100 * (c.suspicious || 0) / tot}%` }} />
          <span className="bg-emerald-500" style={{ width: `${100 * (c.low || 0) / tot}%` }} />
        </div>
      </div>

      <div className="grid md:grid-cols-3 gap-3">
        <div className="card p-0 overflow-hidden md:col-span-2">
          <div className="p-2 text-xs uppercase tracking-wide text-gray-500 border-b">Live event feed — suggested action per threat</div>
          <table className="w-full text-xs">
            <thead><tr className="text-gray-400 text-left"><th className="px-3 py-1">IP / Country</th><th>Level</th><th>Why</th><th>Action</th></tr></thead>
            <tbody>
              {feed.map((e, i) => (
                <tr key={i} className={`border-t border-gray-50 ${blocked.has(e.ip) ? 'opacity-50 line-through' : ''}`}>
                  <td className="px-3 py-1"><span className="font-mono">{e.ip}</span> <span className="text-gray-400">{e.country}</span></td>
                  <td><Pill l={e.level} /></td>
                  <td className="text-gray-500">{(e.reasons || []).join(' · ')}</td>
                  <td>{e.level === 'low' ? <span className="text-gray-400">allow</span> : <Acts t={e} />}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="card p-0 overflow-hidden">
          <div className="p-2 text-xs uppercase tracking-wide text-gray-500 border-b">Top offender IPs</div>
          <table className="w-full text-xs">
            <tbody>
              {(data.top_ips || []).map((t, i) => (
                <tr key={i} className={`border-t border-gray-50 ${blocked.has(t.ip) ? 'opacity-50 line-through' : ''}`}>
                  <td className="px-3 py-1"><span className="font-mono">{t.ip}</span><div className="text-gray-400">{t.country} · {t.score}</div></td>
                  <td><Pill l={t.level} /></td>
                  <td><Acts t={t} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
      <div className="text-[11px] text-gray-500">Per‑app monitor · suggest‑first (known‑bad auto‑eligible) · Block/Deny/Allow/Challenge via Cloudflare, Kill sessions via app hook · shared threat intel across your apps.</div>
      </>)}
    </div>
  );
}
